import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { getDashboard, getUsers } from '../lib/api';
import { useAuth } from '../context/AuthContext';
import { formatDate, getStatusBadgeClass, getPriorityBadgeClass, isOverdue } from '../lib/utils';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Skeleton } from '../components/ui/skeleton';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import {
  CalendarCheck,
  ClipboardText,
  Users,
  UsersFour,
  Warning,
  Clock,
  TrendUp,
  User,
  MapPin,
  ChatCircle
} from '@phosphor-icons/react';

export default function DashboardPage() {
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [users, setUsers] = useState({});
  const [loading, setLoading] = useState(true);
  const [selectedItem, setSelectedItem] = useState(null);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [dashboardRes, usersRes] = await Promise.all([
          getDashboard(),
          getUsers()
        ]);
        setData(dashboardRes.data);
        const usersMap = {};
        usersRes.data.forEach(u => { usersMap[u.id] = u.name; });
        setUsers(usersMap);
      } catch (error) {
        console.error('Failed to fetch dashboard:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  if (loading) {
    return (
      <div className="p-6 space-y-6" data-testid="dashboard-loading">
        <Skeleton className="h-8 w-48" />
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          {[1, 2, 3, 4].map(i => <Skeleton key={i} className="h-24" />)}
        </div>
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <Skeleton className="h-80" />
          <Skeleton className="h-80" />
        </div>
      </div>
    );
  }

  const pipelineTotal = Object.values(data?.pipeline_summary || {}).reduce((a, b) => a + b, 0);

  return (
    <div className="p-6 space-y-6" data-testid="dashboard-page">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold tracking-tight" style={{ fontFamily: 'Manrope' }}>
          Welcome back, {user?.name}
        </h1>
        <p className="text-sm text-zinc-500 mt-1">
          Here's what's happening today
        </p>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-2 md:grid-cols-2 xl:grid-cols-4 gap-3 md:gap-4">
        <Card className="border-zinc-200 rounded-sm" data-testid="stat-total-leads">
          <CardContent className="p-4">
            <div className="flex items-start justify-between">
              <div>
                <p className="text-xs uppercase tracking-[0.1em] font-semibold text-zinc-500">Total Leads</p>
                <p className="text-2xl md:text-3xl font-bold mt-1 tabular-nums">{data?.total_leads || 0}</p>
              </div>
              <div className="p-2 bg-blue-50 rounded-sm">
                <UsersFour size={24} className="text-blue-600" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="border-zinc-200 rounded-sm" data-testid="stat-total-clients">
          <CardContent className="p-4">
            <div className="flex items-start justify-between">
              <div>
                <p className="text-xs uppercase tracking-[0.1em] font-semibold text-zinc-500">Total Clients</p>
                <p className="text-2xl md:text-3xl font-bold mt-1 tabular-nums">{data?.total_clients || 0}</p>
              </div>
              <div className="p-2 bg-green-50 rounded-sm">
                <Users size={24} className="text-green-600" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="border-zinc-200 rounded-sm" data-testid="stat-today-followups">
          <CardContent className="p-4">
            <div className="flex items-start justify-between">
              <div>
                <p className="text-xs uppercase tracking-[0.1em] font-semibold text-zinc-500">Today's Follow-ups</p>
                <p className="text-3xl font-bold mt-1 tabular-nums">{data?.today_follow_ups?.length || 0}</p>
              </div>
              <div className="p-2 bg-amber-50 rounded-sm">
                <CalendarCheck size={24} className="text-amber-600" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="border-zinc-200 rounded-sm" data-testid="stat-overdue">
          <CardContent className="p-4">
            <div className="flex items-start justify-between">
              <div>
                <p className="text-xs uppercase tracking-[0.1em] font-semibold text-zinc-500">Overdue Items</p>
                <p className="text-3xl font-bold mt-1 tabular-nums text-red-600">
                  {(data?.overdue_follow_ups?.length || 0) + (data?.overdue_tasks?.length || 0)}
                </p>
              </div>
              <div className="p-2 bg-red-50 rounded-sm">
                <Warning size={24} className="text-red-600" />
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Main Content */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Today's Follow-ups */}
        <Card className="border-zinc-200 rounded-sm lg:col-span-2" data-testid="today-followups-card">
          <CardHeader className="border-b border-zinc-100 pb-3">
            <CardTitle className="text-base font-semibold flex items-center gap-2">
              <CalendarCheck size={18} className="text-[#002FA7]" />
              Today's Follow-ups
            </CardTitle>
          </CardHeader>
          <CardContent className="p-0">
            {data?.today_follow_ups?.length === 0 ? (
              <div className="p-6 text-center text-zinc-500 text-sm">
                No follow-ups scheduled for today
              </div>
            ) : (
              <div className="divide-y divide-zinc-100">
                {data?.today_follow_ups?.slice(0, 8).map((fu) => (
                  <div
                    key={fu.id}
                    onClick={() => setSelectedItem({ ...fu, _type: 'followup' })}
                    className="flex items-center justify-between p-4 hover:bg-zinc-50 transition-colors cursor-pointer"
                    data-testid={`followup-item-${fu.id}`}
                  >
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2">
                        <p className="font-semibold text-sm text-[#002FA7] truncate">{fu.client_name || 'Contact unavailable'}</p>
                        <span className="text-[10px] bg-zinc-100 text-zinc-600 px-1.5 py-0.5 rounded-sm uppercase tracking-wider font-medium flex-shrink-0">{fu.follow_up_type}</span>
                      </div>
                      <p className="text-sm text-zinc-700 mt-1 line-clamp-1">{fu.description || fu.notes || '—'}</p>
                      <p className="text-xs text-zinc-400 mt-1">
                        {fu.due_time || ''} {fu.due_time && '•'} {users[fu.assigned_to] || 'Unassigned'}
                      </p>
                    </div>
                    <div className="flex items-center gap-2 ml-3 flex-shrink-0">
                      <Badge className={`${getPriorityBadgeClass(fu.priority)} text-xs`}>
                        {fu.priority}
                      </Badge>
                    </div>
                  </div>
                ))}
                {data?.today_follow_ups?.length > 8 && (
                  <Link to="/follow-ups" className="block p-3 text-center text-xs text-[#002FA7] hover:bg-zinc-50 font-medium">
                    View all {data.today_follow_ups.length} follow-ups
                  </Link>
                )}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Pipeline Summary */}
        <Card className="border-zinc-200 rounded-sm" data-testid="pipeline-summary-card">
          <CardHeader className="border-b border-zinc-100 pb-3">
            <CardTitle className="text-base font-semibold flex items-center gap-2">
              <TrendUp size={18} className="text-[#002FA7]" />
              Lead Pipeline
            </CardTitle>
          </CardHeader>
          <CardContent className="p-4 space-y-3">
            {['New', 'Busy / Call After Sometime', 'Follow Up - Brochure Shared', 'Interested for Meeting', 'Meeting Scheduled', 'Meeting Done - Follow Up'].map((status) => (
              <div key={status} className="flex items-center justify-between" data-testid={`pipeline-${status.toLowerCase()}`}>
                <div className="flex items-center gap-2">
                  <Badge className={`${getStatusBadgeClass(status)} text-xs`}>{status}</Badge>
                </div>
                <span className="text-sm font-semibold tabular-nums">
                  {data?.pipeline_summary?.[status] || 0}
                </span>
              </div>
            ))}
            <div className="pt-3 border-t border-zinc-100 flex items-center justify-between">
              <span className="text-xs uppercase tracking-[0.1em] font-semibold text-zinc-500">Total Active</span>
              <span className="text-lg font-bold tabular-nums">{pipelineTotal}</span>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Overdue & Tasks */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Overdue Items */}
        {((data?.overdue_follow_ups?.length || 0) + (data?.overdue_tasks?.length || 0)) > 0 && (
          <Card className="border-red-200 rounded-sm bg-red-50/30" data-testid="overdue-items-card">
            <CardHeader className="border-b border-red-100 pb-3">
              <CardTitle className="text-base font-semibold flex items-center gap-2 text-red-700">
                <Warning size={18} />
                Overdue Items
              </CardTitle>
            </CardHeader>
            <CardContent className="p-0">
              <div className="divide-y divide-red-100">
                {data?.overdue_follow_ups?.slice(0, 5).map((fu) => (
                  <div
                    key={fu.id}
                    onClick={() => setSelectedItem({ ...fu, _type: 'followup' })}
                    className="flex items-center justify-between p-4 hover:bg-red-50 transition-colors cursor-pointer"
                  >
                    <div className="min-w-0 flex-1">
                      <p className="font-semibold text-sm text-red-800">{fu.client_name || 'Contact unavailable'}</p>
                      <p className="text-sm text-red-700 mt-0.5">{fu.follow_up_type}</p>
                      <p className="text-xs text-red-500 mt-1">Due: {formatDate(fu.due_date)} {fu.due_time ? `at ${fu.due_time}` : ''}</p>
                    </div>
                    <Badge className="badge-overdue text-xs flex-shrink-0">Overdue</Badge>
                  </div>
                ))}
                {data?.overdue_tasks?.slice(0, 5).map((task) => (
                  <div
                    key={task.id}
                    onClick={() => setSelectedItem({ ...task, _type: 'task' })}
                    className="flex items-center justify-between p-4 hover:bg-red-50 transition-colors cursor-pointer"
                  >
                    <div className="min-w-0 flex-1">
                      <p className="font-semibold text-sm text-red-800">{task.client_name || 'Contact unavailable'}</p>
                      <p className="text-sm text-red-700 mt-0.5">{task.title || task.task_type}</p>
                      <p className="text-xs text-red-500 mt-1">Due: {formatDate(task.due_date)}</p>
                    </div>
                    <Badge className="badge-overdue text-xs flex-shrink-0">Task Overdue</Badge>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Today's Tasks */}
        <Card className="border-zinc-200 rounded-sm" data-testid="today-tasks-card">
          <CardHeader className="border-b border-zinc-100 pb-3">
            <CardTitle className="text-base font-semibold flex items-center gap-2">
              <ClipboardText size={18} className="text-[#002FA7]" />
              Today's Tasks
            </CardTitle>
          </CardHeader>
          <CardContent className="p-0">
            {data?.today_tasks?.length === 0 ? (
              <div className="p-6 text-center text-zinc-500 text-sm">
                No tasks scheduled for today
              </div>
            ) : (
              <div className="divide-y divide-zinc-100">
                {data?.today_tasks?.slice(0, 8).map((task) => (
                  <div
                    key={task.id}
                    onClick={() => setSelectedItem({ ...task, _type: 'task' })}
                    className="flex items-center justify-between p-4 hover:bg-zinc-50 transition-colors cursor-pointer"
                    data-testid={`task-item-${task.id}`}
                  >
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2">
                        <p className="font-semibold text-sm text-[#002FA7] truncate">{task.client_name || 'Contact unavailable'}</p>
                        <span className="text-[10px] bg-zinc-100 text-zinc-600 px-1.5 py-0.5 rounded-sm uppercase tracking-wider font-medium flex-shrink-0">{task.task_type}</span>
                      </div>
                      <p className="text-sm text-zinc-700 mt-1 line-clamp-1">{task.title || task.description || '—'}</p>
                    </div>
                    <Badge className={`${getPriorityBadgeClass(task.priority)} text-xs flex-shrink-0 ml-3`}>
                      {task.priority}
                    </Badge>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Recent Leads */}
        <Card className="border-zinc-200 rounded-sm" data-testid="recent-leads-card">
          <CardHeader className="border-b border-zinc-100 pb-3">
            <CardTitle className="text-base font-semibold flex items-center gap-2">
              <Clock size={18} className="text-[#002FA7]" />
              Recent Leads
            </CardTitle>
          </CardHeader>
          <CardContent className="p-0">
            {data?.recent_leads?.length === 0 ? (
              <div className="p-6 text-center text-zinc-500 text-sm">
                No recent leads
              </div>
            ) : (
              <div className="divide-y divide-zinc-100">
                {data?.recent_leads?.map((lead) => (
                  <Link
                    key={lead.id}
                    to={`/leads/${lead.id}`}
                    className="flex items-center justify-between p-4 hover:bg-zinc-50 transition-colors"
                    data-testid={`recent-lead-${lead.id}`}
                  >
                    <div>
                      <p className="font-medium text-sm">{lead.full_name}</p>
                      <p className="text-xs text-zinc-500 mt-1">{lead.source} • {formatDate(lead.created_at)}</p>
                    </div>
                    <Badge className={`${getStatusBadgeClass(lead.status)} text-xs`}>
                      {lead.status}
                    </Badge>
                  </Link>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Detail Dialog */}
      <Dialog open={!!selectedItem} onOpenChange={() => setSelectedItem(null)}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="text-base">
              {selectedItem?._type === 'followup' ? 'Follow-up Details' : 'Task Details'}
            </DialogTitle>
          </DialogHeader>
          {selectedItem && (
            <div className="space-y-4 py-2">
              <div className="flex items-center gap-2">
                <User size={16} className="text-zinc-400" />
                <div>
                  <p className="text-xs uppercase tracking-[0.1em] font-semibold text-zinc-500">Client</p>
                  <Link to={selectedItem.lead_id && !selectedItem.client_id ? `/leads/${selectedItem.lead_id}` : `/clients/${selectedItem.client_id}`} className="text-sm font-medium text-[#002FA7] hover:underline">
                    {selectedItem.client_name || 'Unknown'}
                  </Link>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-xs uppercase tracking-[0.1em] font-semibold text-zinc-500">Type</p>
                  <p className="text-sm font-medium mt-0.5">{selectedItem._type === 'followup' ? selectedItem.follow_up_type : selectedItem.task_type}</p>
                </div>
                <div>
                  <p className="text-xs uppercase tracking-[0.1em] font-semibold text-zinc-500">Priority</p>
                  <Badge className={`${getPriorityBadgeClass(selectedItem.priority)} text-xs mt-0.5`}>{selectedItem.priority}</Badge>
                </div>
                <div>
                  <p className="text-xs uppercase tracking-[0.1em] font-semibold text-zinc-500">Due Date</p>
                  <p className="text-sm mt-0.5">{formatDate(selectedItem.due_date)} {selectedItem.due_time ? `at ${selectedItem.due_time}` : ''}</p>
                </div>
                <div>
                  <p className="text-xs uppercase tracking-[0.1em] font-semibold text-zinc-500">Assigned To</p>
                  <p className="text-sm mt-0.5">{users[selectedItem.assigned_to] || 'Unassigned'}</p>
                </div>
              </div>

              {(selectedItem.description || selectedItem.notes) && (
                <div>
                  <p className="text-xs uppercase tracking-[0.1em] font-semibold text-zinc-500">Description</p>
                  <p className="text-sm mt-1 text-zinc-700 whitespace-pre-wrap">{selectedItem.description || selectedItem.notes}</p>
                </div>
              )}

              {selectedItem._type === 'task' && selectedItem.title && selectedItem.title !== selectedItem.task_type && (
                <div>
                  <p className="text-xs uppercase tracking-[0.1em] font-semibold text-zinc-500">Title</p>
                  <p className="text-sm mt-0.5">{selectedItem.title}</p>
                </div>
              )}

              {selectedItem.status && (
                <div>
                  <p className="text-xs uppercase tracking-[0.1em] font-semibold text-zinc-500">Status</p>
                  <Badge className={`${getStatusBadgeClass(selectedItem.status)} text-xs mt-0.5`}>{selectedItem.status}</Badge>
                </div>
              )}

              <div className="pt-3 border-t border-zinc-100">
                <Link
                  to={selectedItem.lead_id && !selectedItem.client_id ? `/leads/${selectedItem.lead_id}` : (selectedItem.client_id ? `/clients/${selectedItem.client_id}` : '/follow-ups')}
                  className="text-sm text-[#002FA7] hover:underline font-medium"
                  onClick={() => setSelectedItem(null)}
                >
                  {selectedItem.lead_id && !selectedItem.client_id ? 'Go to Lead Profile' : 'Go to Client Profile'}
                </Link>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
